import { useEffect, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft, Handshake, Pencil, RefreshCw, Loader2, Copy, Link2,
} from 'lucide-react';
import {
  getPartnerProfile, resendAgentInvite, copyAgentInviteLink, fetchPartnerConnectStatus, getDisplayErrorMessage, deriveInviteStatus, setPartnerServedRegions,
  type PartnerProfile, type PartnerHistoryKind, type PartnerHistoryPage,
} from '@/lib/api';
import { INVITE_LINK_COPY_EN, partnerInviteOpen } from '@/lib/inviteLink';
import { useInviteLinkDialog } from '@/components/ui/InviteLinkDialog';
import type { Column } from '@/components/ui/ResponsiveTable';
import { navigateInApp } from '@/components/sessions/DriverCell';
import { useRecentRoute } from '@/hooks/useRecentRoute';
import {
  CONNECT_STATE_LABELS, PARTNER_TABS, PARTNER_TAB_LABELS, bpsPercent, formatDate, formatDateTime, historyKindsOf, humanize, initialPartnerTab,
  partnerMoney, partnerTabCounts, programRateLabel, showsInternal, type PartnerTab,
} from '@/lib/partnerDirectory';
import { sameRegions } from '@/lib/regionPicker';
import { RegionChips, RegionPicker } from '@/components/regions/RegionPicker';
import { PartnerNotes } from '@/pages/sales-team/PartnerNotes';
import { AgentFormModal } from './AgentFormModal';
import { GoldenEnrolmentCard } from './GoldenEnrolmentCard';
import { AgentStatusBadge } from './AgentStatusBadge';
import { PartnerHistorySection } from './PartnerHistorySection';
import styles from './PartnerDirectory.module.css';

/**
 * A Sales Partner's profile (/agents/:id), the partner equivalent of the
 * driver profile (Warren, 24 October 2026): Details, Jobs, Leads, Chargers,
 * Commissions and payouts, Statements and Activity. The Details tab comes
 * from GET /admin/agents/:id/profile; every other tab reads its sources a
 * page at a time from GET /admin/agents/:id/history/:kind. The tabs and every
 * control follow `capabilities` from the Api, never the viewer's persona.
 * The tab is kept in ?tab= so a link opens the same view.
 *
 * 25 October 2026 (Warren): an Operator sees the history tabs too, for a
 * partner inside its scope, each showing its own tenant's rows; every
 * section renders only when the Api lists its source in
 * capabilities.history_kinds, and EVI-internal cards and columns follow
 * capabilities.internal. "Regions served" is on Details for every viewer,
 * editable when capabilities.edit_regions says so.
 */

interface Props { agentId: string; onNavigate?: (to: string) => void }

// ── Row shapes (apps/api/src/routes/admin/agent-profile.ts) ─────────
interface JobRow { id: string; status: string; customer: string | null; site: string | null; site_type: string | null; units: number;
  self_sourced: boolean; drafted_by: string | null; review_outcome: string | null; expected_install_date: string | null;
  submitted_at: string | null; created_at: string | null; deployment_id: string | null }
interface InstallRow { id: string; status: string; name: string | null; place: string | null; location_type: string | null; chargers: number;
  created_at: string | null; commissioning_authorized_at: string | null; launch_approved_at: string | null; location_id: string | null;
  installed_at: string | null; power_class: string | null }
interface LeadRow { id: string; name: string | null; email: string; phone: string | null; source: string; status: string; created_at: string | null;
  referral_label: string | null; approved_at: string | null; approved_agent_id: string | null }
interface ChargerRow { id: string; identity: string; display_name: string | null; location_name: string | null; is_online: boolean;
  is_active: boolean; last_heartbeat: string | null; commission_bps: number | null; commission_tier: string | null; ordinal: number | null;
  stamped_at: string | null; origination: string | null; created_at: string | null }
interface SiteRow { id: string; address: string; status: string; registered_at: string | null; approved_at: string | null; locations: number; current_owner: boolean }
interface PayoutRow { id: string; session_id: string; status: string; currency: string | null; gross_commission_cents: number;
  net_transfer_cents: number; clawback_applied_cents: number; clawback_debt_recorded_cents: number; reversal_cents_cumulative: number;
  manual_review_required: boolean; stripe_transfer_id: string | null; last_error: string | null; created_at: string | null;
  session_started_at: string | null; charger_identity: string | null; location_name: string | null }
interface LedgerRow { id: string; staff_name: string; source_kind: string; category: string; period: string; earning_at: string | null;
  currency: string; basis_cents: number; rate_bps: number; amount_cents: number; status: string; held_cents: number }
interface StatementRow { id: string; period: string; currency: string | null; sessions: number; gross_cents: number; clawback_netted_cents: number;
  net_cents: number; transferred_cents: number; pending_cents: number; reversed_cents: number; clawback_debt_cents: number }
interface TeamStatementRow { id: string; period: string | null; staff_name: string | null; closed_at: string | null; partner_lines: number;
  partner_totals: Array<{ currency: string; amount_cents: number }> }
interface ActivityRow { id: string; action: string; resource: string; created_at: string | null; actor: string; details: Array<{ key: string; value: string }> }
interface MailRow { id: string; kind: string; program: string; recipient: string; status: string; boundary: number | null; attempts: number;
  sent_at: string | null; created_at: string | null; last_error: string | null }
interface DeliverabilityRow { id: string; kind: string; email: string | null; bounce_type: string | null; status_code: string | null;
  complaint_type: string | null; action: string; created_at: string | null }

const PAYOUT_FILTERS = [
  { key: 'all', label: 'All' }, { key: 'transferred', label: 'Transferred' }, { key: 'pending_failed', label: 'Pending or failed' },
  { key: 'reversed', label: 'Reversed' }, { key: 'clawback', label: 'Clawback' },
] as const;

function Row({ label, children }: { label: string; children: ReactNode }) {
  return <div className={styles.dlRow}><dt>{label}</dt><dd>{children}</dd></div>;
}
function Card({ title, children }: { title: string; children: ReactNode }) {
  return <section className={styles.card}><h2 className={styles.cardTitle}>{title}</h2>{children}</section>;
}
const yesNo = (v: boolean) => (v ? 'Yes' : 'No');

export function PartnerProfilePage({ agentId, onNavigate }: Props) {
  const go = onNavigate ?? navigateInApp;
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['partner-profile', agentId],
    queryFn: async () => {
      try { return await getPartnerProfile(agentId); }
      catch (err) { throw new Error(await getDisplayErrorMessage(err, 'Could not load this Sales Partner.')); }
    },
    retry: false,
  });
  const profile = q.data;
  useRecentRoute(profile ? `${profile.agent.name} (Sales Partner)` : '');
  const allowed = profile?.capabilities.tabs ?? ['details'];
  // The tab asked for in ?tab=; checked against the Api's list below.
  const [tab, setTabState] = useState<PartnerTab>(() => initialPartnerTab(
    typeof window === 'undefined' ? null : new URLSearchParams(window.location.search).get('tab'), PARTNER_TABS));
  // Once the Api says which tabs this viewer has, a tab it lacks falls back.
  useEffect(() => {
    if (profile && !allowed.includes(tab)) setTabState('details');
  }, [profile, allowed, tab]);
  const setTab = (next: PartnerTab) => {
    setTabState(next);
    const url = new URL(window.location.href);
    if (next === 'details') url.searchParams.delete('tab'); else url.searchParams.set('tab', next);
    window.history.replaceState({}, '', `${url.pathname}${url.search}`);
  };
  const [editOpen, setEditOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const inviteLink = useInviteLinkDialog();
  const reload = () => qc.invalidateQueries({ queryKey: ['partner-profile', agentId] });

  const back = (
    <button type="button" className={styles.backBtn} onClick={() => go('/agents')}>
      <ArrowLeft size={14} /> Back to Sales Partners
    </button>
  );

  if (q.isLoading) {
    return <div className={styles.page}>{back}<div className={styles.loading}><Loader2 size={16} className={styles.spin} /> Loading Sales Partner</div></div>;
  }
  if (q.error || !profile) {
    return <div className={styles.page}>{back}<p className={styles.error} role="alert">{q.error?.message ?? 'Could not load this Sales Partner.'}</p></div>;
  }

  const { agent, capabilities, connect } = profile;
  const invite = deriveInviteStatus(agent);
  const tabCount = partnerTabCounts(profile.counts);
  const kinds = historyKindsOf(capabilities);
  const internal = showsInternal(capabilities);

  async function resend() {
    setBusy(true); setMessage('');
    try {
      const r = await resendAgentInvite(agentId);
      setMessage(r.invite_email?.ok ? `Invite emailed to ${r.invite_email.to ?? agent.email}.` : 'A new invite link is ready, but the email did not go. Use Copy invite link to send it yourself.');
      await reload();
    } catch (err) { setMessage(await getDisplayErrorMessage(err, 'Could not re-send the invite.')); }
    finally { setBusy(false); }
  }
  async function refreshConnect() {
    setBusy(true); setMessage('');
    try { await fetchPartnerConnectStatus(agentId, true); await reload(); setMessage('Payout status checked with Stripe.'); }
    catch (err) { setMessage(await getDisplayErrorMessage(err, 'Could not check payout status.')); }
    finally { setBusy(false); }
  }
  const copy = (text: string) => void navigator.clipboard.writeText(text).then(() => setMessage('Copied.')).catch(() => setMessage('Select and copy the text.'));

  const connectTone = connect.state === 'ready' ? styles.chipOk : connect.state === 'restricted' ? styles.chipErr : connect.state === 'not_started' ? '' : styles.chipWarn;

  return (
    <div className={styles.page}>
      {back}
      <header className={styles.profileHeader}>
        <span className={styles.headerIcon}><Handshake size={20} /></span>
        <div className={styles.headerInfo}>
          <h1 className={styles.title}>{agent.name}{!agent.active && <span className={styles.muted}> (inactive)</span>}</h1>
          <p className={styles.subtitle}>{[agent.contact_name && agent.contact_name !== agent.name ? agent.contact_name : null, agent.email].filter(Boolean).join(' · ')}</p>
          <div className={styles.chipRow}>
            <span className={`${styles.chip} ${agent.program_info?.program === 'golden' ? styles.chipGold : ''}`}>{agent.program_info?.name ?? 'Sales Partner'}</span>
            <span className={`${styles.chip} ${agent.active ? styles.chipOk : ''}`}>{agent.active ? 'Active' : 'Inactive'}</span>
            <span className={`${styles.chip} ${invite === 'accepted' ? styles.chipOk : invite === 'expired' ? styles.chipWarn : styles.chipInfo}`}>Invite {invite}</span>
            <span className={`${styles.chip} ${connectTone}`}>Payouts: {CONNECT_STATE_LABELS[connect.state]}</span>
            {profile.sales_rep && <span className={styles.chip}>Rep: {profile.sales_rep.name}</span>}
            {internal && !profile.sales_rep && <span className={styles.chip}>House book</span>}
          </div>
        </div>
        <div className={styles.headerActions}>
          {/* Re-send invite and Copy invite link (29 October 2026): one rule for
              both, on the capability the Api computes for the resend's gate,
              which the link endpoint runs too. */}
          {capabilities.resend_invite && partnerInviteOpen(agent) && (
            <>
              <button type="button" className={styles.secondaryBtn} disabled={busy} onClick={() => void resend()}>
                <RefreshCw size={13} /> Re-send invite
              </button>
              <button type="button" className={styles.secondaryBtn} disabled={busy || inviteLink.busyFor !== null}
                onClick={() => void inviteLink.open({ email: agent.email, name: agent.contact_name || agent.name }, () => copyAgentInviteLink(agentId))}>
                <Link2 size={13} /> {INVITE_LINK_COPY_EN.action}
              </button>
            </>
          )}
          {capabilities.edit && (
            <button type="button" className={styles.secondaryBtn} onClick={() => setEditOpen(true)}>
              <Pencil size={13} /> Edit identity
            </button>
          )}
        </div>
      </header>
      {message && <p className={styles.status} role="status">{message}</p>}

      <div className={styles.tabs} role="tablist" aria-label="Sales Partner profile">
        {allowed.map((t) => {
          const key = t as PartnerTab;
          return (
            <button key={key} type="button" role="tab" aria-selected={tab === key}
              className={`${styles.tab} ${tab === key ? styles.tabActive : ''}`} onClick={() => setTab(key)}>
              <span>{PARTNER_TAB_LABELS[key]}</span>
              {tabCount[key] !== undefined && <span className={styles.tabCount}>{tabCount[key]}</span>}
            </button>
          );
        })}
      </div>

      {tab === 'details' && (
        <DetailsTab profile={profile} busy={busy} onRefreshConnect={() => void refreshConnect()} onCopy={copy}
          onChanged={() => void reload()} />
      )}
      {!internal && tab !== 'details' && <p className={styles.note}>Showing your organisation&rsquo;s records for this partner only.</p>}
      {tab === 'jobs' && <JobsTab agentId={agentId} go={go} kinds={kinds} />}
      {tab === 'leads' && <LeadsTab agentId={agentId} go={go} kinds={kinds} />}
      {tab === 'chargers' && <ChargersTab agentId={agentId} go={go} kinds={kinds} />}
      {tab === 'commissions' && <CommissionsTab agentId={agentId} go={go} kinds={kinds} internal={internal} />}
      {tab === 'statements' && <StatementsTab agentId={agentId} kinds={kinds} />}
      {tab === 'activity' && <ActivityTab agentId={agentId} kinds={kinds} />}

      {editOpen && (
        <AgentFormModal mode="edit" agent={agent} onClose={() => { setEditOpen(false); void reload(); }}
          onSaved={async () => { setEditOpen(false); await reload(); }} />
      )}
      {inviteLink.dialog}
    </div>
  );
}

// ── Details ─────────────────────────────────────────────────────────

function DetailsTab({ profile, busy, onRefreshConnect, onCopy, onChanged }: {
  profile: PartnerProfile; busy: boolean; onRefreshConnect: () => void; onCopy: (text: string) => void; onChanged: () => void;
}) {
  const { agent, capabilities, connect } = profile;
  const invite = deriveInviteStatus(agent);
  const act = agent.activity;
  const referral = profile.referral_link;
  const internal = showsInternal(capabilities);
  return (
    <>
      {agent.debt_review_required && (
        <p className={styles.alert} role="alert">Partner exit has outstanding commission debt. Operator review required; no invoice is sent automatically.</p>
      )}
      <div className={styles.grid}>
        <Card title="Partner">
          <dl className={styles.dl}>
            <Row label="Business">{agent.name}</Row>
            <Row label="Contact">{agent.contact_name ?? 'Not recorded'}</Row>
            <Row label="Email"><a className={styles.nameLink} href={`mailto:${agent.email}`}>{agent.email}</a></Row>
            <Row label="Phone">{agent.phone ? <a className={styles.nameLink} href={`tel:${agent.phone}`}>{agent.phone}</a> : 'Not recorded'}</Row>
            <Row label="Added">{formatDate(agent.created_at)}</Row>
            <Row label="Partner id">{agent.id}</Row>
          </dl>
          {agent.notes && <pre className={styles.notes}>{agent.notes}</pre>}
        </Card>

        <Card title="Program and commission">
          <dl className={styles.dl}>
            <Row label="Program">{agent.program_info?.name ?? 'Sales Partner'}</Row>
            {agent.program_info?.pending.map((p) => (
              <Row key={p.effective_at} label="Scheduled">{p.program === 'golden' ? 'Golden Partner' : 'Sales Partner'} from {formatDate(p.effective_at)}</Row>
            ))}
            <Row label="Program rate">{programRateLabel(agent.program_info)}</Row>
            <Row label="Qualifying chargers">{agent.deployed_paid_count ?? 0}</Row>
            <Row label="Qualified">{agent.qualified_at ? formatDate(agent.qualified_at) : 'Not yet (4 pay-to-use chargers)'}</Row>
            <Row label="Pace (365 days)">{act ? `${act.deployed_last_window} of ${act.min}${agent.qualified_at ? (act.on_pace ? ', on pace' : `, behind by ${act.behind_by}`) : ''}` : 'Not available'}</Row>
            <Row label="Outstanding clawback">{`$${(agent.outstanding_clawback_cents / 100).toFixed(2)}`}</Row>
          </dl>
          <p className={styles.note}>The program rate is what a new qualifying launch is stamped with. Payouts use each charger&rsquo;s stamped rate.</p>
        </Card>

        <Card title="Invite and account">
          <dl className={styles.dl}>
            <Row label="Invite">{humanize(invite)}</Row>
            <Row label="Invite sent">{formatDateTime(agent.invite_sent_at)}</Row>
            {invite !== 'accepted' && <Row label="Invite expires">{formatDateTime(agent.invite_token_expires_at)}</Row>}
            <Row label="Accepted">{formatDateTime(agent.invite_accepted_at)}</Row>
            {profile.user_account && <>
              <Row label="Sign-in email">{profile.user_account.email}</Row>
              <Row label="Account">{profile.user_account.is_active ? 'Active' : 'Deactivated'}</Row>
              <Row label="Last sign-in">{formatDateTime(profile.user_account.last_login)}</Row>
            </>}
          </dl>
          {internal && !profile.user_account && <p className={styles.note}>No sign-in account yet. One is created when the partner accepts the invite.</p>}
        </Card>

        <RegionsCard agentId={agent.id} regions={agent.served_regions ?? []} canEdit={capabilities.edit_regions === true} onSaved={onChanged} />

        <Card title="Payouts (Stripe Connect)">
          <dl className={styles.dl}>
            <Row label="Status">{CONNECT_STATE_LABELS[connect.state]}</Row>
            {connect.state !== 'not_started' && <>
              <Row label="Details submitted">{yesNo(connect.details_submitted)}</Row>
              <Row label="Payouts enabled">{yesNo(connect.payouts_enabled)}</Row>
              <Row label="Transfers active">{yesNo(connect.transfers_active)}</Row>
              <Row label="Information due">{yesNo(connect.information_due)}</Row>
              <Row label="Bank country">{connect.payout_country ?? 'Not recorded'}</Row>
              <Row label="Last checked">{formatDateTime(connect.checked_at)}</Row>
            </>}
          </dl>
          {connect.state !== 'not_started' && capabilities.refresh_connect && (
            <div className={styles.actions}>
              <button type="button" className={styles.secondaryBtn} disabled={busy} onClick={onRefreshConnect}><RefreshCw size={13} /> Check with Stripe now</button>
            </div>
          )}
          {connect.state === 'not_started' && <p className={styles.note}>The partner has not started payout setup.</p>}
        </Card>

        {internal && (
          <Card title="Sales rep">
            <dl className={styles.dl}>
              <Row label="Current">{profile.sales_rep ? `${profile.sales_rep.name}${profile.sales_rep.employed ? '' : ' (no longer employed)'}` : 'EVI house book'}</Row>
            </dl>
            {profile.assignment_history.length > 0 && (
              <ul className={styles.list}>
                {profile.assignment_history.map((h, i) => (
                  <li key={`${h.effective_at}-${i}`} className={styles.listItem}>
                    <span>{h.staff_name ?? 'EVI house book'}{h.reason ? <span className={styles.muted}>: {h.reason}</span> : null}</span>
                    <span className={`${styles.muted} ${styles.num}`}>from {formatDate(h.effective_at)}</span>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        )}

        {internal && (
          <Card title="Application">
            {profile.application ? (
              <>
                <dl className={styles.dl}>
                  <Row label="Received">{formatDateTime(profile.application.submitted_at)}</Row>
                  <Row label="Form">{profile.application.source === 'sp_qualification' ? 'Sales Partner (website)' : humanize(profile.application.source)}</Row>
                  <Row label="Name">{profile.application.name ?? 'Not recorded'}</Row>
                  <Row label="Phone">{profile.application.phone ?? 'Not recorded'}</Row>
                  <Row label="Regions">{<RegionChips codes={profile.application.served_regions} empty="Not given" />}</Row>
                  <Row label="Referred by">{profile.application.referral_label ?? 'Not recorded'}</Row>
                  <Row label="Approved">{profile.application.approved_at ? formatDateTime(profile.application.approved_at) : 'Not recorded'}</Row>
                  {profile.application.approval_reason && <Row label="Approval note">{profile.application.approval_reason}</Row>}
                </dl>
                {profile.application.notes && <pre className={styles.notes}>{profile.application.notes}</pre>}
              </>
            ) : (
              <p className={styles.note}>No website application is recorded for this partner. Partners added by hand, or before applications were recorded, have none.</p>
            )}
          </Card>
        )}

        {internal && (
          <Card title="Partner's referral link">
            {referral ? (
              <>
                <p className={styles.code}>{referral.apply_url}</p>
                <div className={styles.actions}>
                  <button type="button" className={styles.secondaryBtn} onClick={() => onCopy(referral.apply_url)}><Copy size={13} /> Copy link</button>
                </div>
                <p className={styles.note}>The partner&rsquo;s own link, from their profile. Applications through it are credited to the partner&rsquo;s rep.</p>
              </>
            ) : (
              <p className={styles.note}>The partner has not created a referral link. Partners create one from their profile.</p>
            )}
          </Card>
        )}

        {internal && (
          <Card title="Team">
            {profile.team.length ? (
              <ul className={styles.list}>
                {profile.team.map((m) => (
                  <li key={m.id} className={styles.listItem}>
                    <span className={styles.wrapAnywhere}>{m.name ?? m.email}{m.name && <span className={styles.muted}> {m.email}</span>}</span>
                    <span className={styles.muted}>{humanize(m.status)}{m.can_order ? ', can order' : ''}</span>
                  </li>
                ))}
              </ul>
            ) : <p className={styles.note}>No team members.</p>}
          </Card>
        )}
      </div>

      <GoldenEnrolmentCard agentId={agent.id} onChanged={onChanged} />
      <PartnerNotes kind="partner" id={agent.id} />
      {!internal && <p className={styles.note}>The other tabs show your organisation&rsquo;s deployments, chargers, commissions, statement and audit log for this partner. Jobs, applications, site claims, the sales team&rsquo;s commission and program email are kept by EVI.</p>}
    </>
  );
}

// ── History tabs ────────────────────────────────────────────────────

type Go = (to: string) => void;
type Kinds = readonly PartnerHistoryKind[];

/** The partner's states and provinces, on Details for every viewer; editable
 *  when the Api says this viewer may (PUT /admin/agents/:id/served-regions). */
function RegionsCard({ agentId, regions, canEdit, onSaved }: { agentId: string; regions: string[]; canEdit: boolean; onSaved: () => void }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<string[]>(regions);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  useEffect(() => { if (!editing) setDraft(regions); }, [regions, editing]);
  async function save() {
    setBusy(true); setMessage('');
    try {
      const r = await setPartnerServedRegions(agentId, draft);
      setEditing(false);
      setMessage(r.unchanged ? 'No change to save.' : 'Regions saved.');
      onSaved();
    } catch (err) { setMessage(await getDisplayErrorMessage(err, 'Could not save the regions.')); }
    finally { setBusy(false); }
  }
  return (
    <Card title="Regions served">
      <RegionChips codes={editing ? draft : regions} empty="No regions recorded yet." />
      {canEdit && !editing && (
        <div className={styles.actions}>
          <button type="button" className={styles.secondaryBtn} onClick={() => { setMessage(''); setEditing(true); }}><Pencil size={13} /> Edit regions</button>
        </div>
      )}
      {canEdit && editing && (
        <div className={styles.regionEditor}>
          <RegionPicker value={draft} onChange={setDraft} label="Regions served" emptyLabel="Choose regions" />
          <div className={styles.actions}>
            <button type="button" className={styles.primaryBtn} disabled={busy || sameRegions(draft, regions)} onClick={() => void save()}>
              {busy ? <><Loader2 size={13} className={styles.spin} /> Saving</> : 'Save regions'}
            </button>
            <button type="button" className={styles.secondaryBtn} disabled={busy} onClick={() => { setEditing(false); setDraft(regions); }}>Cancel</button>
          </div>
        </div>
      )}
      <p className={styles.note}>The states and provinces this partner serves. Filled from the website application on approval; the partner list filters on it.</p>
      {message && <p className={styles.status} role="status">{message}</p>}
    </Card>
  );
}

function JobsTab({ agentId, go, kinds }: { agentId: string; go: Go; kinds: Kinds }) {
  const jobs: Column<JobRow>[] = [
    { key: 'created', header: 'Created', mobile: 'primary', cell: (j) => <span className={styles.num}>{formatDate(j.created_at)}</span> },
    { key: 'customer', header: 'Customer', mobile: 'secondary', cell: (j) => j.customer ?? <span className={styles.muted}>Not recorded</span> },
    { key: 'status', header: 'Status', cell: (j) => <span className={styles.chip}>{humanize(j.status)}</span> },
    { key: 'site', header: 'Site', cell: (j) => [j.site, j.site_type ? humanize(j.site_type) : null].filter(Boolean).join(', ') || 'Not recorded' },
    { key: 'units', header: 'Units', align: 'right', cell: (j) => <span className={styles.num}>{j.units}{j.self_sourced ? ' (own hardware)' : ''}</span> },
    { key: 'drafted', header: 'Drafted by', mobile: 'hidden', cell: (j) => j.drafted_by ?? 'Not recorded' },
    { key: 'submitted', header: 'Submitted', cell: (j) => <span className={styles.num}>{formatDate(j.submitted_at)}</span> },
    { key: 'install', header: 'Install date', mobile: 'hidden', cell: (j) => j.expected_install_date ?? 'Not set' },
  ];
  const installs: Column<InstallRow>[] = [
    { key: 'created', header: 'Registered', mobile: 'primary', cell: (s) => <span className={styles.num}>{formatDate(s.created_at)}</span> },
    { key: 'name', header: 'Site', mobile: 'secondary', cell: (s) => [s.name, s.place].filter(Boolean).join(', ') || 'Not recorded' },
    { key: 'status', header: 'Status', cell: (s) => <span className={styles.chip}>{humanize(s.status)}</span> },
    { key: 'chargers', header: 'Chargers', align: 'right', cell: (s) => <span className={styles.num}>{s.chargers}</span> },
    { key: 'installed', header: 'Installed', cell: (s) => <span className={styles.num}>{s.installed_at ? formatDate(s.installed_at) : 'Not confirmed'}</span> },
    { key: 'launch', header: 'Launch approved', cell: (s) => <span className={styles.num}>{formatDate(s.launch_approved_at)}</span> },
  ];
  return (
    <>
      {kinds.includes('jobs') && <PartnerHistorySection<JobRow> agentId={agentId} kind="jobs" title="Jobs" columns={jobs}
        hint="New jobs the partner or its team drafted, newest first. Open one for its timeline and orders."
        empty="No jobs yet." onRowClick={(j) => go(`/sales-jobs/${j.id}`)} />}
      {kinds.includes('installs') && <PartnerHistorySection<InstallRow> agentId={agentId} kind="installs" title="Deployments and installs" columns={installs}
        hint="Sites registered for this partner, with the install the partner confirmed."
        empty="No deployments yet." onRowClick={(s) => go(`/deployments/${s.id}`)} />}
    </>
  );
}

function LeadsTab({ agentId, go, kinds }: { agentId: string; go: Go; kinds: Kinds }) {
  if (!kinds.includes('leads')) return null;
  const columns: Column<LeadRow>[] = [
    { key: 'received', header: 'Received', mobile: 'primary', cell: (l) => <span className={styles.num}>{formatDate(l.created_at)}</span> },
    { key: 'name', header: 'Name', mobile: 'secondary', cell: (l) => l.name ?? l.email },
    { key: 'email', header: 'Email', cell: (l) => <span className={styles.wrapAnywhere}>{l.email}</span> },
    { key: 'phone', header: 'Phone', cell: (l) => l.phone ?? 'Not recorded' },
    { key: 'status', header: 'Status', cell: (l) => <span className={styles.chip}>{humanize(l.status)}</span> },
    { key: 'referral', header: 'Referred as', mobile: 'hidden', cell: (l) => l.referral_label ?? 'Not recorded' },
    {
      key: 'approved', header: 'Outcome',
      cell: (l) => (l.approved_agent_id
        ? <a className={styles.nameLink} href={`/agents/${l.approved_agent_id}`} onClick={(e) => { e.preventDefault(); go(`/agents/${l.approved_agent_id}`); }}>Approved, open partner</a>
        : <span className={styles.muted}>Not approved yet</span>),
    },
  ];
  return (
    <PartnerHistorySection<LeadRow> agentId={agentId} kind="leads" title="Applications this partner referred" columns={columns}
      hint="Sales Partner applications that came in through this partner's referral link. Review them in Referral applications."
      empty="No applications have come in through this partner's link." />
  );
}

function ChargersTab({ agentId, go, kinds }: { agentId: string; go: Go; kinds: Kinds }) {
  const chargers: Column<ChargerRow>[] = [
    { key: 'identity', header: 'Charger', mobile: 'primary', cell: (c) => <span className={styles.stack}><span>{c.display_name ?? c.identity}</span>{c.display_name && <span className={styles.muted}>{c.identity}</span>}</span> },
    { key: 'location', header: 'Location', mobile: 'secondary', cell: (c) => c.location_name ?? 'Not recorded' },
    { key: 'online', header: 'Status', cell: (c) => <span className={`${styles.chip} ${!c.is_active ? '' : c.is_online ? styles.chipOk : styles.chipWarn}`}>{!c.is_active ? 'Inactive' : c.is_online ? 'Online' : 'Offline'}</span> },
    { key: 'ordinal', header: 'Order', align: 'right', cell: (c) => <span className={styles.num}>{c.ordinal ?? 'Not stamped'}</span> },
    { key: 'rate', header: 'Commission', align: 'right', cell: (c) => <span className={styles.num}>{c.commission_bps === null ? 'Free charger' : bpsPercent(c.commission_bps)}</span> },
    { key: 'stamped', header: 'Deployed', cell: (c) => <span className={styles.num}>{formatDate(c.stamped_at ?? c.created_at)}</span> },
    { key: 'origination', header: 'Origination', mobile: 'hidden', cell: (c) => humanize(c.origination) },
  ];
  const sites: Column<SiteRow>[] = [
    { key: 'address', header: 'Address', mobile: 'primary', cell: (s) => <span className={styles.wrapAnywhere}>{s.address}</span> },
    { key: 'status', header: 'Status', cell: (s) => <span className={styles.chip}>{humanize(s.status)}</span> },
    { key: 'registered', header: 'Registered', cell: (s) => <span className={styles.num}>{formatDate(s.registered_at)}</span> },
    { key: 'approved', header: 'Approved', cell: (s) => <span className={styles.num}>{formatDate(s.approved_at)}</span> },
    { key: 'locations', header: 'Locations', align: 'right', cell: (s) => <span className={styles.num}>{s.locations}</span> },
    { key: 'owner', header: 'Claim', cell: (s) => (s.current_owner ? 'Held by this partner' : 'First registered, now held by another') },
  ];
  return (
    <>
      {kinds.includes('chargers') && <PartnerHistorySection<ChargerRow> agentId={agentId} kind="chargers" title="Chargers" columns={chargers}
        hint="Chargers assigned to this partner (Network Origin). Order and rate are stamped when a pay-to-use charger is deployed and never change."
        empty="No chargers are assigned to this partner." onRowClick={(c) => go(`/chargers/${c.id}`)} />}
      {kinds.includes('sites') && <PartnerHistorySection<SiteRow> agentId={agentId} kind="sites" title="Site claims" columns={sites}
        hint="Sites this partner registered or holds."
        empty="No site claims." />}
    </>
  );
}

function CommissionsTab({ agentId, go, kinds, internal }: { agentId: string; go: Go; kinds: Kinds; internal: boolean }) {
  const [status, setStatus] = useState<string>('all');
  const payouts: Column<PayoutRow>[] = [
    { key: 'date', header: 'Date', mobile: 'primary', cell: (r) => <span className={styles.num} title={formatDateTime(r.created_at)}>{formatDate(r.created_at)}</span> },
    { key: 'where', header: 'Charger and site', mobile: 'secondary', cell: (r) => [r.charger_identity, r.location_name].filter(Boolean).join(', ') || 'Not recorded' },
    { key: 'status', header: 'Status', cell: (r) => <AgentStatusBadge status={r.status} manualReview={r.manual_review_required} /> },
    { key: 'gross', header: 'Gross', align: 'right', cell: (r) => <span className={styles.num}>{partnerMoney(r.gross_commission_cents, r.currency)}</span> },
    { key: 'net', header: 'Net', align: 'right', cell: (r) => <span className={styles.num}>{partnerMoney(r.net_transfer_cents, r.currency)}</span> },
    {
      key: 'adjust', header: 'Reversed or clawback', align: 'right',
      cell: (r) => (r.reversal_cents_cumulative || r.clawback_debt_recorded_cents || r.clawback_applied_cents ? (
        <span className={styles.stack}>
          {r.reversal_cents_cumulative > 0 && <span className={styles.num}>{partnerMoney(r.reversal_cents_cumulative, r.currency)} reversed</span>}
          {r.clawback_debt_recorded_cents > 0 && <span className={styles.num}>{partnerMoney(r.clawback_debt_recorded_cents, r.currency)} clawback</span>}
          {r.clawback_applied_cents > 0 && <span className={`${styles.num} ${styles.muted}`}>{partnerMoney(r.clawback_applied_cents, r.currency)} netted</span>}
        </span>
      ) : <span className={styles.muted}>None</span>),
    },
    // EVI's Stripe transfer and its error text: only when the Api sends them (capabilities.internal).
    ...(internal ? [
      { key: 'transfer', header: 'Transfer', mobile: 'hidden' as const, cell: (r: PayoutRow) => (r.stripe_transfer_id ? <span className={styles.code}>{r.stripe_transfer_id}</span> : <span className={styles.muted}>Not sent</span>) },
      { key: 'error', header: 'Last error', mobile: 'hidden' as const, cell: (r: PayoutRow) => r.last_error ?? <span className={styles.muted}>None</span> },
    ] : []),
  ];
  const ledger: Column<LedgerRow>[] = [
    { key: 'earned', header: 'Earned', mobile: 'primary', cell: (l) => <span className={styles.num}>{formatDate(l.earning_at)}</span> },
    { key: 'rep', header: 'Rep', mobile: 'secondary', cell: (l) => l.staff_name },
    { key: 'category', header: 'Category', cell: (l) => humanize(l.category) },
    { key: 'basis', header: 'Basis', align: 'right', cell: (l) => <span className={styles.num}>{partnerMoney(l.basis_cents, l.currency)}</span> },
    { key: 'rate', header: 'Rate', align: 'right', cell: (l) => <span className={styles.num}>{bpsPercent(l.rate_bps)}</span> },
    { key: 'amount', header: 'Amount', align: 'right', cell: (l) => <span className={styles.num}>{partnerMoney(l.amount_cents, l.currency)}</span> },
    { key: 'status', header: 'Status', cell: (l) => <span className={styles.chip}>{humanize(l.status)}</span> },
    { key: 'period', header: 'Month', mobile: 'hidden', cell: (l) => <span className={styles.num}>{l.period}</span> },
  ];
  const summary = (first: PartnerHistoryPage<PayoutRow> | undefined) => {
    const s = first?.summary;
    if (!s || !Object.keys(s).length) return null;
    return (
      <div className={styles.summary}>
        {Object.entries(s).sort((a, b) => b[1] - a[1]).map(([k, n]) => <span key={k} className={`${styles.chip} ${styles.num}`}>{humanize(k)}: {n}</span>)}
      </div>
    );
  };
  return (
    <>
      {kinds.includes('payouts') && <div className={styles.filterChips} role="group" aria-label="Filter commissions by status">
        {PAYOUT_FILTERS.map((f) => (
          <button key={f.key} type="button" className={`${styles.filterChip} ${status === f.key ? styles.filterChipActive : ''}`}
            aria-pressed={status === f.key} onClick={() => setStatus(f.key)}>{f.label}</button>
        ))}
      </div>}
      {kinds.includes('payouts') && <PartnerHistorySection<PayoutRow> agentId={agentId} kind="payouts" status={status} title="Commission payouts" columns={payouts}
        hint="One row per paid session on this partner's chargers: the commission, what was transferred, and any reversal or clawback. Each currency stays separate. Open a row for the session."
        empty={status === 'all' ? 'No commission yet. Rows appear once sessions on this partner’s pay-to-use chargers are paid.' : 'No commission rows match this filter.'}
        onRowClick={(r) => go(`/sessions/${r.session_id}`)} header={summary} />}
      {kinds.includes('ledger') && <PartnerHistorySection<LedgerRow> agentId={agentId} kind="ledger" title="Sales team commission from this partner" columns={ledger}
        hint="What EVI's sales reps earned from this partner's activity (activation, orders, sessions). Paid with the sales payout run, not to the partner."
        empty="No sales team commission from this partner." />}
    </>
  );
}

function StatementsTab({ agentId, kinds }: { agentId: string; kinds: Kinds }) {
  const months: Column<StatementRow>[] = [
    { key: 'period', header: 'Month', mobile: 'primary', cell: (b) => <span className={styles.num}>{b.period}{b.currency ? '' : ' (currency not recorded)'}</span> },
    { key: 'currency', header: 'Currency', mobile: 'secondary', cell: (b) => b.currency ?? 'Not recorded' },
    { key: 'sessions', header: 'Sessions', align: 'right', cell: (b) => <span className={styles.num}>{b.sessions}</span> },
    { key: 'gross', header: 'Gross', align: 'right', cell: (b) => <span className={styles.num}>{partnerMoney(b.gross_cents, b.currency)}</span> },
    { key: 'netted', header: 'Clawback netted', align: 'right', cell: (b) => <span className={styles.num}>{partnerMoney(b.clawback_netted_cents, b.currency)}</span> },
    { key: 'net', header: 'Net', align: 'right', cell: (b) => <span className={styles.num}>{partnerMoney(b.net_cents, b.currency)}</span> },
    { key: 'transferred', header: 'Transferred', align: 'right', cell: (b) => <span className={styles.num}>{partnerMoney(b.transferred_cents, b.currency)}</span> },
    { key: 'pending', header: 'Pending', align: 'right', cell: (b) => <span className={styles.num}>{partnerMoney(b.pending_cents, b.currency)}</span> },
    { key: 'reversed', header: 'Reversed', align: 'right', mobile: 'hidden', cell: (b) => <span className={styles.num}>{partnerMoney(b.reversed_cents, b.currency)}</span> },
    { key: 'debt', header: 'Clawback debt', align: 'right', mobile: 'hidden', cell: (b) => <span className={styles.num}>{partnerMoney(b.clawback_debt_cents, b.currency)}</span> },
  ];
  const team: Column<TeamStatementRow>[] = [
    { key: 'period', header: 'Month', mobile: 'primary', cell: (s) => <span className={styles.num}>{s.period ?? 'Not recorded'}</span> },
    { key: 'rep', header: 'Rep', mobile: 'secondary', cell: (s) => s.staff_name ?? 'Not recorded' },
    { key: 'closed', header: 'Closed', cell: (s) => <span className={styles.num}>{formatDate(s.closed_at)}</span> },
    { key: 'lines', header: 'Lines from partner', align: 'right', cell: (s) => <span className={styles.num}>{s.partner_lines}</span> },
    { key: 'amount', header: 'Amount from partner', align: 'right', cell: (s) => <span className={styles.stack}>{s.partner_totals.map((t) => <span key={t.currency} className={styles.num}>{partnerMoney(t.amount_cents, t.currency)}</span>)}</span> },
  ];
  return (
    <>
      {kinds.includes('statements') && <PartnerHistorySection<StatementRow> agentId={agentId} kind="statements" title="Monthly commission statement" columns={months}
        hint="The partner's commission by month (UTC month the commission was recorded) and currency, from the payout rows. Twelve months a page."
        empty="No commission months yet." />}
      {kinds.includes('team_statements') && <PartnerHistorySection<TeamStatementRow> agentId={agentId} kind="team_statements" title="Sales team statements that include this partner" columns={team}
        hint="Closed monthly statements of EVI's sales reps that carry lines earned from this partner."
        empty="No closed sales team statement includes this partner." />}
    </>
  );
}

function ActivityTab({ agentId, kinds }: { agentId: string; kinds: Kinds }) {
  const audit: Column<ActivityRow>[] = [
    { key: 'when', header: 'When', mobile: 'primary', cell: (r) => <span className={styles.num}>{formatDateTime(r.created_at)}</span> },
    { key: 'action', header: 'Action', mobile: 'secondary', cell: (r) => humanize(r.action) },
    { key: 'actor', header: 'By', cell: (r) => r.actor },
    { key: 'resource', header: 'Record', mobile: 'hidden', cell: (r) => <span className={styles.code}>{r.resource}</span> },
    { key: 'details', header: 'Details', cell: (r) => (r.details.length
      ? <span className={styles.stack}>{r.details.map((d) => <span key={d.key} className={styles.wrapAnywhere}><span className={styles.muted}>{humanize(d.key)}:</span> {d.value}</span>)}</span>
      : <span className={styles.muted}>None</span>) },
  ];
  const mail: Column<MailRow>[] = [
    { key: 'created', header: 'Queued', mobile: 'primary', cell: (m) => <span className={styles.num}>{formatDateTime(m.created_at)}</span> },
    { key: 'kind', header: 'Email', mobile: 'secondary', cell: (m) => `${humanize(m.kind)}${m.boundary ? ` (charger ${m.boundary})` : ''}, ${humanize(m.program)}` },
    { key: 'status', header: 'Status', cell: (m) => <span className={`${styles.chip} ${m.status === 'sent' ? styles.chipOk : m.status === 'failed' ? styles.chipErr : ''}`}>{humanize(m.status)}</span> },
    { key: 'sent', header: 'Sent', cell: (m) => <span className={styles.num}>{formatDateTime(m.sent_at)}</span> },
    { key: 'to', header: 'To', mobile: 'hidden', cell: (m) => <span className={styles.wrapAnywhere}>{m.recipient}</span> },
    { key: 'error', header: 'Last error', mobile: 'hidden', cell: (m) => m.last_error ?? <span className={styles.muted}>None</span> },
  ];
  const deliverability: Column<DeliverabilityRow>[] = [
    { key: 'when', header: 'When', mobile: 'primary', cell: (f) => <span className={styles.num}>{formatDateTime(f.created_at)}</span> },
    { key: 'kind', header: 'Kind', mobile: 'secondary', cell: (f) => humanize(f.kind) },
    { key: 'email', header: 'Address', cell: (f) => <span className={styles.wrapAnywhere}>{f.email ?? 'Not recorded'}</span> },
    { key: 'type', header: 'Type', cell: (f) => [f.bounce_type, f.status_code, f.complaint_type].filter(Boolean).join(', ') || 'Not recorded' },
    { key: 'action', header: 'Result', cell: (f) => humanize(f.action) },
  ];
  return (
    <>
      {kinds.includes('activity') && <PartnerHistorySection<ActivityRow> agentId={agentId} kind="activity" title="Audit log" columns={audit}
        hint="Recorded changes to this partner, its jobs, team, application and installs."
        empty="No audit entries yet." />}
      {kinds.includes('mail') && <PartnerHistorySection<MailRow> agentId={agentId} kind="mail" title="Program email" columns={mail}
        hint="Welcome and milestone emails. Invite emails and application acknowledgements are not stored: invite dates are on Details and each re-send is in the audit log."
        empty="No program email yet." />}
      {kinds.includes('deliverability') && <PartnerHistorySection<DeliverabilityRow> agentId={agentId} kind="deliverability" title="Bounces and complaints" columns={deliverability}
        hint="Email delivery problems reported for this partner's addresses."
        empty="No bounces or complaints." />}
    </>
  );
}
